import { useCallback, useEffect, useId, useMemo, useState, type ReactElement } from 'react';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';
import { denialReason } from '@/lib/permissions';
import { ROLE_LABELS, roleLabel } from '@/lib/roles';
import { useUserGroups } from '@/lib/user-groups';
import { cx } from '@/lib/cx';
import type {
  NotificationDefaultRow,
  NotificationEventInfo,
  NotificationFrequency,
  NotificationScope,
  UserRole,
} from '@/lib/types';
import {
  Badge,
  Button,
  Field,
  Panel,
  PanelBody,
  PanelHeader,
  Select,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Tooltip,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import {
  TARGET_SEPARATOR,
  errorText,
  targetId,
  useNotificationCatalogue,
  type NotificationTarget,
} from './notification-targets';

/*
 * TEAM DEFAULTS — `GET|PUT /notifications/defaults` (grant `settings-notifications`).
 *
 * One row per (role or user group) × (module, submodule or event). `null` on a
 * field = inherit. The PUT is a WHOLE-SET replace across EVERY subject, so the
 * draft holds all subjects' rows and the picker only chooses which to show.
 * A row whose four fields are all inherit is not sent.
 *
 * Locked targets (every covered event locked) cannot be switched off: the
 * server answers 422 `NOTIFICATION_LOCKED`, so their cells are disabled.
 *
 * NO-ACCESS TARGETS (owner 2026-10-22, "notifications follow permission sets").
 * The response's `roleAccess[roleSlug][grantModule]` is each role's read scope
 * per catalogue `grantModule`. For a ROLE subject, a target whose every covered
 * event's grantModule is `none` for that role is shown Off and disabled, with
 * the reason on a wrapper Tooltip; the server refuses switching it on with 422
 * `NOTIFICATION_NO_ACCESS`. A `true` already stored on such a row is sent as
 * inherit (`null`) so an old choice cannot make the whole-set PUT unsaveable.
 * Group subjects are never narrowed (groups span roles). `roleAccess` absent
 * (older API), an event with no `grantModule`, or a module/role missing from
 * the map → `all`, i.e. behaves as before.
 */

const ROLE_ORDER: UserRole[] = ['viewer', 'basic_user', 'advanced_user', 'admin', 'super_admin'];

type AccessScope = 'all' | 'own' | 'none';
type RoleAccess = Record<string, Record<string, AccessScope>>;

/** `grantModule` read defensively: the catalogue type may not carry it yet. */
function grantModuleOf(event: NotificationEventInfo): string | null {
  const value = (event as NotificationEventInfo & { grantModule?: string | null }).grantModule;
  return typeof value === 'string' && value !== '' ? value : null;
}

function eventAccess(event: NotificationEventInfo, role: string, roleAccess: RoleAccess | null): AccessScope {
  if (!roleAccess || role === 'super_admin') return 'all';
  const grantModule = grantModuleOf(event);
  if (grantModule === null) return 'all';
  return roleAccess[role]?.[grantModule] ?? 'all';
}

/** Every covered event is out of the role's read access. */
function roleHasNoAccess(target: NotificationTarget, role: string, roleAccess: RoleAccess | null): boolean {
  return (
    target.events.length > 0 && target.events.every((event) => eventAccess(event, role, roleAccess) === 'none')
  );
}

type Tri = '' | 'on' | 'off';
type Field4 = 'emailEnabled' | 'inboxEnabled' | 'frequency' | 'scope';

function rowKey(row: Pick<NotificationDefaultRow, 'subjectType' | 'subjectKey' | 'targetType' | 'targetKey'>) {
  return `${row.subjectType}|${row.subjectKey}|${row.targetType}|${row.targetKey}`;
}

function isEmpty(row: NotificationDefaultRow): boolean {
  return (
    row.emailEnabled === null && row.inboxEnabled === null && row.frequency === null && row.scope === null
  );
}

/** Stable comparison form: inherit-only rows dropped, sorted by identity. */
function normalise(rows: NotificationDefaultRow[]): NotificationDefaultRow[] {
  return rows
    .filter((row) => !isEmpty(row))
    .map((row) => ({
      subjectType: row.subjectType,
      subjectKey: row.subjectKey,
      targetType: row.targetType,
      targetKey: row.targetKey,
      emailEnabled: row.emailEnabled,
      inboxEnabled: row.inboxEnabled,
      frequency: row.frequency,
      scope: row.scope,
    }))
    .sort((a, b) => (rowKey(a) < rowKey(b) ? -1 : rowKey(a) > rowKey(b) ? 1 : 0));
}

function triOf(value: boolean | null): Tri {
  return value === null ? '' : value ? 'on' : 'off';
}

function boolOf(value: string): boolean | null {
  return value === 'on' ? true : value === 'off' ? false : null;
}

function parseSubject(value: string): { subjectType: 'role' | 'group'; subjectKey: string } | null {
  const colon = value.indexOf(':');
  if (colon <= 0) return null;
  const type = value.slice(0, colon);
  if (type !== 'role' && type !== 'group') return null;
  return { subjectType: type, subjectKey: value.slice(colon + 1) };
}

export function NotificationDefaultsCard() {
  const { ability } = useAuth();
  const headingId = useId();
  const denial = denialReason(ability, 'update', 'settings-notifications');
  const canWrite = denial === null && ability.canUpdate('settings-notifications');

  const [saved, setSaved] = useState<NotificationDefaultRow[] | null>(null);
  const [draft, setDraft] = useState<NotificationDefaultRow[]>([]);
  const [roleAccess, setRoleAccess] = useState<RoleAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [subject, setSubject] = useState<string>('role:basic_user');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const catalogue = useNotificationCatalogue(!forbidden);
  const groups = useUserGroups(!forbidden);

  const load = useCallback((signal?: AbortSignal) => {
    return api
      .getNotificationDefaults(signal)
      .then((response) => {
        if (signal?.aborted) return;
        const rows = normalise(response.data.defaults);
        setSaved(rows);
        setDraft(rows);
        setRoleAccess(response.data.roleAccess ?? null);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (signal?.aborted) return;
        if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(true);
          return;
        }
        if (caught instanceof ApiError && caught.status === 401) return;
        setLoadError(caught instanceof Error ? caught.message : 'The team defaults could not be read.');
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const parsedSubject = parseSubject(subject);

  const draftByKey = useMemo(() => {
    const map = new Map<string, NotificationDefaultRow>();
    for (const row of draft) map.set(rowKey(row), row);
    return map;
  }, [draft]);

  const dirty = useMemo(
    () => saved !== null && JSON.stringify(normalise(draft)) !== JSON.stringify(saved),
    [draft, saved],
  );

  /** Subjects (other than the shown one) carrying unsaved changes, for the save bar. */
  const changedSubjects = useMemo(() => {
    if (saved === null) return 0;
    const ident = (rows: NotificationDefaultRow[]) => {
      const map = new Map<string, string>();
      for (const row of rows) {
        const key = `${row.subjectType}:${row.subjectKey}`;
        map.set(key, `${map.get(key) ?? ''}${JSON.stringify(row)}`);
      }
      return map;
    };
    const before = ident(saved);
    const after = ident(normalise(draft));
    const keys = new Set([...before.keys(), ...after.keys()]);
    let count = 0;
    for (const key of keys) if (before.get(key) !== after.get(key)) count += 1;
    return count;
  }, [draft, saved]);

  function update(target: NotificationTarget, field: Field4, value: string) {
    if (!parsedSubject || !canWrite) return;
    const identity = {
      subjectType: parsedSubject.subjectType,
      subjectKey: parsedSubject.subjectKey,
      targetType: target.type,
      targetKey: target.key,
    };
    const key = rowKey(identity);
    setSaveError(null);
    setDraft((current) => {
      const existing = current.find((row) => rowKey(row) === key) ?? {
        ...identity,
        emailEnabled: null,
        inboxEnabled: null,
        frequency: null,
        scope: null,
      };
      const next: NotificationDefaultRow = { ...existing };
      if (field === 'emailEnabled') next.emailEnabled = boolOf(value);
      else if (field === 'inboxEnabled') next.inboxEnabled = boolOf(value);
      else if (field === 'frequency') next.frequency = value ? (value as NotificationFrequency) : null;
      else next.scope = value ? (value as NotificationScope) : null;
      const rest = current.filter((row) => rowKey(row) !== key);
      return isEmpty(next) ? rest : [...rest, next];
    });
  }

  function moduleLabelOf(target: NotificationTarget): string {
    return (
      catalogue.catalogue?.modules.find((module) => module.key === target.moduleKey)?.label ??
      target.path.split(TARGET_SEPARATOR)[0] ??
      target.path
    );
  }

  function noAccessReason(target: NotificationTarget, role: string): string {
    return `The ${roleLabel(role)} permission set does not include ${moduleLabelOf(target)}.`;
  }

  function save() {
    if (saving || !dirty || !canWrite) return;
    setSaving(true);
    setSaveError(null);
    // A stored `on` for a target the role cannot read is sent back unchanged: the
    // server refuses only a channel newly switched ON, so the old choice is kept
    // and applies again if the role regains access.
    const payload = normalise(draft);
    api
      .saveNotificationDefaults({ defaults: payload })
      .then((response) => {
        const rows = normalise(response.data.defaults);
        setSaved(rows);
        setDraft(rows);
        if (response.data.roleAccess !== undefined) setRoleAccess(response.data.roleAccess);
      })
      .catch((caught: unknown) => {
        if (caught instanceof ApiError && caught.code === 'NOTIFICATION_NO_ACCESS') {
          const index = Number.parseInt(/\d+/.exec(caught.details?.[0]?.path ?? '')?.[0] ?? '', 10);
          const row = Number.isInteger(index) ? payload[index] : undefined;
          const target = row ? catalogue.byId.get(targetId(row.targetType, row.targetKey)) : undefined;
          if (row && target && row.subjectType === 'role') {
            setSaveError(noAccessReason(target, row.subjectKey));
            return;
          }
        }
        setSaveError(errorText(caught, 'The team defaults were not saved.'));
      })
      .finally(() => setSaving(false));
  }

  function discard() {
    if (saving || saved === null) return;
    setDraft(saved);
    setSaveError(null);
  }

  function toggleModule(moduleKey: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(moduleKey)) next.delete(moduleKey);
      else next.add(moduleKey);
      return next;
    });
  }

  const visibleTargets = catalogue.targets.filter(
    (target) => target.depth === 0 || expanded.has(target.moduleKey),
  );

  function rowOf(target: NotificationTarget): NotificationDefaultRow | undefined {
    if (!parsedSubject) return undefined;
    return draftByKey.get(
      rowKey({
        subjectType: parsedSubject.subjectType,
        subjectKey: parsedSubject.subjectKey,
        targetType: target.type,
        targetKey: target.key,
      }),
    );
  }

  function cells(target: NotificationTarget) {
    const row = rowOf(target);
    const disabled = !canWrite || saving;
    const noAccess =
      parsedSubject?.subjectType === 'role' && roleHasNoAccess(target, parsedSubject.subjectKey, roleAccess);
    const noAccessText = noAccess && parsedSubject ? noAccessReason(target, parsedSubject.subjectKey) : undefined;
    const lockedReason = target.locked ? 'Locked notifications are always on.' : undefined;
    const scopeReason = !target.clientScoped ? 'Scope applies to client notifications only.' : undefined;
    const channelReason = denial ?? noAccessText ?? lockedReason;
    const label = target.path;
    const wrap = (reason: string | null | undefined, control: ReactElement) =>
      reason ? (
        <Tooltip label={reason} className="block">
          {control}
        </Tooltip>
      ) : (
        <span className="block">{control}</span>
      );
    return {
      email: wrap(
        channelReason,
        <Select
          aria-label={`${label}: Email`}
          value={noAccess ? 'off' : triOf(row?.emailEnabled ?? null)}
          disabled={disabled || target.locked || noAccess}
          onChange={(event) => update(target, 'emailEnabled', event.target.value)}
        >
          <option value="">Inherit</option>
          <option value="on">On</option>
          <option value="off">Off</option>
        </Select>,
      ),
      inbox: wrap(
        channelReason,
        <Select
          aria-label={`${label}: In-app`}
          value={noAccess ? 'off' : triOf(row?.inboxEnabled ?? null)}
          disabled={disabled || target.locked || noAccess}
          onChange={(event) => update(target, 'inboxEnabled', event.target.value)}
        >
          <option value="">Inherit</option>
          <option value="on">On</option>
          <option value="off">Off</option>
        </Select>,
      ),
      frequency: wrap(
        channelReason,
        <Select
          aria-label={`${label}: Frequency`}
          value={row?.frequency ?? ''}
          disabled={disabled || target.locked || noAccess}
          onChange={(event) => update(target, 'frequency', event.target.value)}
        >
          <option value="">Inherit</option>
          <option value="instant">Instant</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </Select>,
      ),
      scope: wrap(
        denial ?? noAccessText ?? scopeReason,
        <Select
          aria-label={`${label}: Scope`}
          value={row?.scope ?? ''}
          disabled={disabled || !target.clientScoped || noAccess}
          onChange={(event) => update(target, 'scope', event.target.value)}
        >
          <option value="">Inherit</option>
          <option value="mine">My records</option>
          <option value="all">All clients</option>
        </Select>,
      ),
    };
  }

  function nameCell(target: NotificationTarget) {
    const name = target.path.split(' › ').pop() ?? target.path;
    if (target.depth === 0) {
      const open = expanded.has(target.moduleKey);
      return (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => toggleModule(target.moduleKey)}
          className="flex min-h-9 items-center gap-1.5 bg-transparent p-0 text-left text-sm font-semibold text-fg"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={cx('h-4 w-4 shrink-0 text-fg-subtle transition-transform', open && 'rotate-90')}
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
          {name}
          {target.locked && <Badge tone="neutral">Locked</Badge>}
        </button>
      );
    }
    return (
      <span
        className={cx(
          'flex items-center gap-2',
          target.depth === 1 ? 'pl-5 font-medium text-fg' : 'pl-10 text-fg-secondary',
        )}
      >
        {name}
        {target.locked && <Badge tone="neutral">Locked</Badge>}
      </span>
    );
  }

  const ready = saved !== null && catalogue.catalogue !== null;

  return (
    <Panel aria-labelledby={headingId}>
      <PanelHeader id={headingId} title="Team defaults" />
      <PanelBody className="flex flex-col gap-4">
        {loadError && (
          <Notice tone="danger" role="alert" title="Couldn’t read the team defaults.">
            <span className="min-w-0 [overflow-wrap:anywhere]">{loadError}</span>
          </Notice>
        )}
        {catalogue.error && (
          <Notice tone="danger" role="alert" title="Couldn’t read the notification catalogue.">
            <span className="min-w-0 [overflow-wrap:anywhere]">{catalogue.error}</span>
          </Notice>
        )}

        {forbidden ? (
          <p className="m-0 text-xs text-fg-subtle">
            Your permission set does not include this. An administrator can change that.
          </p>
        ) : (loading || catalogue.loading) && !ready ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : !ready ? null : (
          <>
            <div className="max-w-sm">
              <Field label="Defaults for">
                {(fieldProps) => (
                  <Select
                    {...fieldProps}
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                  >
                    <optgroup label="Roles">
                      {ROLE_ORDER.map((role) => (
                        <option key={role} value={`role:${role}`}>
                          {ROLE_LABELS[role]}
                        </option>
                      ))}
                    </optgroup>
                    {groups.records.length > 0 && (
                      <optgroup label="User groups">
                        {groups.records.map((group) => (
                          <option key={group.id} value={`group:${group.slug}`}>
                            {group.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </Select>
                )}
              </Field>
              {groups.failed && (
                <p role="alert" className="mt-1 mb-0 text-xs text-danger">
                  The user groups could not be read.
                </p>
              )}
            </div>

            <div className="max-md:hidden">
              <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
                <Table>
                  <THead>
                    <TR>
                      <TH>Notification</TH>
                      <TH>Email</TH>
                      <TH>In-app</TH>
                      <TH>Frequency</TH>
                      <TH>Scope</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {visibleTargets.map((target) => {
                      const c = cells(target);
                      return (
                        <TR key={`${target.type}:${target.key}`}>
                          <TD>{nameCell(target)}</TD>
                          <TD className="w-32">{c.email}</TD>
                          <TD className="w-32">{c.inbox}</TD>
                          <TD className="w-36">{c.frequency}</TD>
                          <TD className="w-40">{c.scope}</TD>
                        </TR>
                      );
                    })}
                  </TBody>
                </Table>
              </TableWrap>
            </div>

            <ul className="m-0 flex list-none flex-col divide-y divide-border p-0 md:hidden">
              {visibleTargets.map((target) => {
                const c = cells(target);
                return (
                  <li key={`${target.type}:${target.key}`} className="flex flex-col gap-2 py-3">
                    {nameCell(target)}
                    <div className="grid grid-cols-2 gap-2">
                      {(
                        [
                          ['Email', c.email],
                          ['In-app', c.inbox],
                          ['Frequency', c.frequency],
                          ['Scope', c.scope],
                        ] as const
                      ).map(([cellLabel, control]) => (
                        <div key={cellLabel} className="grid content-start gap-1.5">
                          <span aria-hidden="true" className="text-xs font-medium text-fg-secondary">
                            {cellLabel}
                          </span>
                          {control}
                        </div>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>

            {saveError && (
              <Notice tone="danger" role="alert" title="Couldn’t save the team defaults.">
                <span className="min-w-0 [overflow-wrap:anywhere]">{saveError}</span>
              </Notice>
            )}

            {canWrite && dirty && (
              <div className="sticky bottom-0 z-20 -mx-4 flex flex-wrap items-center justify-end gap-2 border-t border-border bg-surface px-4 py-3">
                <span className="mr-auto text-xs text-fg-secondary">
                  {changedSubjects === 1 ? 'Unsaved changes' : `Unsaved changes in ${changedSubjects} teams`}
                </span>
                <Button variant="ghost" onClick={discard} disabled={saving}>
                  Discard
                </Button>
                <Button onClick={save} loading={saving}>
                  Save
                </Button>
              </div>
            )}
          </>
        )}
      </PanelBody>
    </Panel>
  );
}
